import { Button } from "pepsico-ds";
import {
  getFileImportStatusLabel,
  useFileImportStatus,
} from "../../hooks/useFileImportStatus";
import { downloadImportErrorsCsv } from "./downloadImportErrorsCsv";
import "./FileImportStatus.scss";

/**
 * Shows the status of a file import using the shared tracker.
 * Pass the status query, the key of the record in its result, and its variables
 * (including uploadId). Renders nothing while `variables` is null.
 * `failedMessage` customises the FAILED text; `renderExtra(upload)` adds
 * service-specific details (e.g. reward name, draw).
 */
const FileImportStatus = ({
  query,
  dataKey,
  variables,
  onTerminal,
  failedMessage = "The file was not imported.",
  renderExtra,
}) => {
  const { upload, timedOut, error } = useFileImportStatus({
    query,
    dataKey,
    variables,
    onTerminal,
  });

  if (!variables) return null;
  if (error) {
    return (
      <div className="file-import-status" data-testid="import-status-error">
        <p>Could not retrieve the import status. Check back later.</p>
      </div>
    );
  }
  if (!upload) return null;

  const { uploadStatus, importedRows, totalRows, rejectedRows, errors } = upload;
  const errorCount = upload.errorCount ?? errors?.length ?? 0;

  return (
    <div className="file-import-status" data-testid="import-status">
      <p>
        <strong>Status:</strong> {getFileImportStatusLabel(upload)}
        {upload.fileName ? ` (${upload.fileName})` : ""}
      </p>
      {renderExtra?.(upload)}
      {timedOut && <p>Still processing, check back later.</p>}
      {uploadStatus === "COMPLETED" && (
        <p>
          Imported {importedRows} of {totalRows} rows
          {rejectedRows != null ? `, ${rejectedRows} rejected` : ""}.
        </p>
      )}
      {uploadStatus === "FAILED" && (
        <p>
          {failedMessage}
          {errorCount > 0 ? " Fix the errors below and upload the file again." : ""}
        </p>
      )}
      {errors?.length > 0 && (
        <>
          <p className="font-xs">
            {upload.errorsTruncated
              ? `Showing the first ${errors.length} of ${errorCount} errors`
              : `${errorCount} error(s)`}
          </p>
          <div className="file-import-errors">
            <table>
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Column</th>
                  <th>Value</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e, index) => (
                  <tr key={`${e.line}-${e.column}-${index}`}>
                    <td>{e.line}</td>
                    <td>{e.column}</td>
                    <td>{e.value}</td>
                    <td>{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            variant="secondary"
            text="Download errors (CSV)"
            onClick={() => downloadImportErrorsCsv(errors, upload.fileName)}
          />
        </>
      )}
    </div>
  );
};

export default FileImportStatus;
